/*
 * WHERE TO? — the screen behind the orientation pill.
 *
 * On a map whose premise is that the dark is what you have not surveyed, this is the one place that
 * answers "so where have I been" — and the one that takes you somewhere. Theirs reaches it by
 * tapping the pill at the foot of the map (universe-v8.dc.html:114); ours had the same pill in the
 * same place, saying the same kind of thing, and doing nothing at all.
 *
 * IT WAS TITLED "WHERE YOU HAVE BEEN" AND THAT WAS HALF THE SCREEN. The heading contradicted the
 * sentence directly beneath it — "Return anywhere you have walked. Places you have not are shown
 * from a distance" — and a reader who took a row headed "where you have been" was as likely to be
 * going somewhere new. Theirs asks "Where to?", which covers both halves and is what a control that
 * MOVES the camera should say. The record is still there, in the count on every row.
 *
 * Every row is a neighbourhood with how much of it has been walked. Taking one RETURNS to it if the
 * reader knows it, and LOOKS AT IT FROM HERE if they do not — their two actions, and the distinction
 * the whole fog exists to make.
 *
 * It is a dialog rather than a page: it covers the map, it closes on Escape or on the backdrop, and
 * focus goes into it so a keyboard reader is not left behind on the map underneath.
 */
import { useEffect, useRef } from 'react';
import { useLang } from '../i18n/lang';
import type { Region } from './regions';

export interface MapTravelProps {
  regions: readonly Region[];
  /** Go to a region — the caller decides whether that is a walk or a look. */
  onGo: (id: string, known: boolean) => void;
  onClose: () => void;
}

export function MapTravel({ regions, onGo, onClose }: MapTravelProps): React.JSX.Element {
  const { t } = useLang();
  const first = useRef<HTMLButtonElement | null>(null);

  /*
   * Focus goes into the dialog on open, so a keyboard reader is not left behind on the map.
   *
   * ESCAPE IS HANDLED ON THE DIALOG, NOT ON THE WINDOW, and that was learned the hard way. A window
   * listener registered in an effect keyed on `onClose` — an inline arrow, so a new identity every
   * render — is added and removed on every render of the app above it, and in the browser it ended up
   * removed with nothing to re-add it: the effect ran, the dialog stayed mounted, and the keystroke
   * reached window while this listener was not on it. Every unit test passed, because a test renders
   * once and never re-renders.
   *
   * Focus is inside the dialog, so the keydown bubbles through it. A modal's key handling belongs on
   * the modal, where it cannot be lost.
   */
  useEffect(() => { first.current?.focus(); }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== 'Escape') return;
    /* The map behind also closes things on Escape; this one is on top and answers first. */
    e.stopPropagation();
    onClose();
  }

  return (
    <div
      className="travel"
      role="dialog"
      aria-modal="true"
      aria-label={t('travel.title')}
      data-map-chrome
      onKeyDown={onKeyDown}
      /* The backdrop closes it. The card inside stops the click, so taking a row is not also a
         dismissal — one gesture, one meaning. */
      onClick={onClose}
    >
      <div className="travel-card" onClick={e => { e.stopPropagation(); }}>
        <h2 className="travel-title">{t('travel.title')}</h2>
        <p className="travel-lead">{t('travel.lead')}</p>
        {regions.length === 0
          ? <p className="travel-empty">{t('travel.nowhere')}</p>
          : (
            <ul className="travel-rows">
              {regions.map((region, i) => (
                <li key={region.id}>
                  <button
                    type="button"
                    ref={i === 0 ? first : undefined}
                    className={region.known ? 'travel-row travel-row--known' : 'travel-row'}
                    onClick={() => onGo(region.id, region.known)}
                  >
                    <span className="travel-row-head">
                      <span className="travel-row-name">{region.name}</span>
                      <span className="travel-row-action">
                        {t(region.known ? 'travel.return' : 'travel.look')}
                      </span>
                    </span>
                    <span className="travel-row-foot">
                      {/* No text of its own: the count beside it says the number, and a length in
                          pixels is not something a screen reader can use. */}
                      <span className="travel-bar" aria-hidden="true">
                        <span style={{ width: `${Math.round(region.share * 100)}%` }} />
                      </span>
                      <span className="travel-row-stat">
                        {t('travel.walked', { walked: region.walked, total: region.total })}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  );
}
